import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { ClipboardList, CheckCircle2, FileText, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function Forms() {
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteRow, setDeleteRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.get("/forms/templates"),
        api.get("/forms/submissions"),
      ]);
      setTemplates(t.data);
      setSubmissions(s.data);
    } catch { toast.error("Failed to load forms"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const drafts = submissions.filter((s) => s.status === "draft");
  const completed = submissions.filter((s) => s.status === "completed");

  const onDelete = async () => {
    if (!deleteRow) return;
    try {
      await api.delete(`/forms/submissions/${deleteRow.id}`);
      toast.success("Removed");
      setDeleteRow(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <div data-testid="forms-page">
      <PageHeader
        eyebrow="[10] Forms"
        title="Forms"
        subtitle="Standard operating procedure checklists. Complete a form onsite, sign it off, and it becomes a permanent audit record."
      />
      <div className="p-4 sm:p-6 lg:p-8 space-y-8">
        {/* Available templates */}
        <section>
          <div className="uppercase-label mb-3">Available Forms</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {templates.map((t) => (
              <div key={t.slug} data-testid={`template-${t.slug}`} className="border border-[#E5E0D5] bg-white p-5 flex flex-col">
                <div className="flex items-start gap-3 mb-3">
                  <ClipboardList className="w-5 h-5 text-[#F37B2A] flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[#0F0F0F]">{t.name}</div>
                    <div className="text-xs font-mono text-[#7A7A7A] mt-1">{t.section_count} sections</div>
                  </div>
                </div>
                <p className="text-sm text-[#5A5A5A] mb-4 flex-1">{t.description}</p>
                <Link
                  to={`/forms/new/${t.slug}`}
                  data-testid={`start-${t.slug}`}
                  className="inline-flex items-center justify-center gap-2 rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase h-10 px-4 self-start"
                >
                  Start form <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
            {templates.length === 0 && !loading && (
              <div className="col-span-full font-mono text-xs text-[#7A7A7A]">No templates configured.</div>
            )}
          </div>
        </section>

        {/* Drafts */}
        {drafts.length > 0 && (
          <section>
            <div className="uppercase-label mb-3">In Progress ({drafts.length})</div>
            <SubmissionsTable rows={drafts} onDelete={setDeleteRow} showDelete />
          </section>
        )}

        {/* Completed */}
        <section>
          <div className="uppercase-label mb-3">Completed & Audit Records ({completed.length})</div>
          <SubmissionsTable rows={completed} onDelete={setDeleteRow} showDelete={false} completed />
          {completed.length === 0 && !loading && (
            <div className="border border-[#E5E0D5] bg-white p-10 text-center">
              <FileText className="w-6 h-6 text-[#9A9A9A] mx-auto mb-2" />
              <div className="font-mono text-xs text-[#9A9A9A]">No completed forms yet.</div>
            </div>
          )}
        </section>
      </div>

      <AlertDialog open={!!deleteRow} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">
              Delete {deleteRow?.status === "completed" ? "audit record" : "draft"}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A]">
              {deleteRow?.status === "completed"
                ? "This is a completed audit record. Deleting it is admin-only and permanent."
                : "This draft will be discarded."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-submission-btn" onClick={onDelete} className="rounded-none bg-[#B91C1C] hover:bg-[#cc2200] text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SubmissionsTable({ rows, onDelete, showDelete, completed }) {
  return (
    <div className="border border-[#E5E0D5] bg-white overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#E5E0D5] text-[#7A7A7A]">
            <Th>Form</Th>
            <Th>Client</Th>
            <Th>User</Th>
            <Th align="right">Progress</Th>
            <Th>{completed ? "Completed" : "Last Updated"}</Th>
            <Th>{completed ? "By" : "Started By"}</Th>
            <Th align="right"></Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const pct = s.check_total > 0 ? Math.round((s.check_done / s.check_total) * 100) : 0;
            const client = s.header_inputs?.client || "—";
            const userName = s.header_inputs?.user_name || "—";
            return (
              <tr key={s.id} data-testid={`submission-${s.id}`} className="border-b border-[#E5E0D5] hover:bg-[#F0EDE3]">
                <Td>
                  <Link to={`/forms/${s.id}`} className="text-[#0F0F0F] hover:underline underline-offset-2 inline-flex items-center gap-2">
                    {completed && <CheckCircle2 className="w-3.5 h-3.5 text-[#2D7A4F]" />}
                    {s.template_name}
                  </Link>
                </Td>
                <Td><span className="text-[#3A3A3A]">{client}</span></Td>
                <Td><span className="text-[#3A3A3A]">{userName}</span></Td>
                <Td align="right">
                  <div className="flex items-center gap-2 justify-end">
                    <div className="w-20 h-1 bg-[#E5E0D5] relative">
                      <div className={`absolute inset-y-0 left-0 ${completed ? "bg-[#2D7A4F]" : "bg-[#F37B2A]"}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="font-mono text-[10px] text-[#5A5A5A] w-14 text-right">{s.check_done}/{s.check_total}</span>
                  </div>
                </Td>
                <Td><span className="font-mono text-xs text-[#5A5A5A]">{fmtDate(completed ? s.completed_at : s.updated_at)}</span></Td>
                <Td><span className="font-mono text-xs text-[#5A5A5A]">{completed ? s.completed_by : s.created_by}</span></Td>
                <Td align="right">
                  <div className="flex items-center justify-end gap-1">
                    <Link
                      to={`/forms/${s.id}`}
                      data-testid={`open-submission-${s.id}`}
                      className="font-mono text-[10px] uppercase tracking-wider text-[#5A5A5A] hover:text-[#0F0F0F] px-2"
                    >{completed ? "View" : "Resume"}</Link>
                    {showDelete && (
                      <button
                        onClick={() => onDelete(s)}
                        data-testid={`delete-submission-${s.id}`}
                        className="p-1.5 text-[#7A7A7A] hover:text-[#B91C1C]"
                        title="Delete"
                      ><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                </Td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={7} className="px-4 py-8 text-center font-mono text-xs text-[#9A9A9A]">None yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th className={`px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A] text-${align} font-medium`}>{children}</th>;
}
function Td({ children, align = "left" }) {
  return <td className={`px-4 py-3 text-${align}`}>{children}</td>;
}
